import type {Profile} from "~/types/models"
import {getProfile} from "~/composables/services"

// Routes that must stay reachable while the profile is still incomplete:
// the auth pages, the whole register chain, and the completion page itself
// (otherwise the guard would redirect /profile/info onto itself).
export const PROFILE_GUARD_EXEMPT = [
	'login', 'login-otp',
	'register', 'register-otp', 'register-select-sport', 'register-leveling', 'register-complete',
	'profile-info', 'profile-new-password', 'profile-otp',
	'reset-password',
]

export const isProfileFilled = (profile: Partial<Profile> | null | undefined) =>
	!!profile
	&& !!`${profile.firstName ?? ''}`.trim()
	&& !!`${profile.lastName ?? ''}`.trim()

export const PROFILE_INCOMPLETE_MESSAGE = 'برای ادامه، لطفاً نام و نام خانوادگی خود را وارد کنید.'

let lastNotifiedAt = 0

// the guard can fire on back-to-back navigations, so don't stack identical toasts
export const notifyProfileIncomplete = () => {
	if (Date.now() - lastNotifiedAt < 3000) return
	lastNotifiedAt = Date.now()
	const {$toast} = useNuxtApp() as any
	$toast?.info(PROFILE_INCOMPLETE_MESSAGE)
}

// dedupes the confirmation request when several navigations fire at once
let pendingCheck: Promise<boolean> | null = null

export const markProfileComplete = (value = true) => {
	useLocalStorage('profileComplete', false).value = value
	pendingCheck = null
}

export const checkProfileComplete = async (): Promise<boolean> => {
	const complete = useLocalStorage('profileComplete', false)
	if (complete.value) return true

	const user = useLocalStorage<any>('user', {})
	if (isProfileFilled(user.value?.profile)) {
		complete.value = true
		return true
	}

	const userId = useAppCookie('userId')
	const token = useAppCookie('token')
	// not logged in: app.vue owns that redirect, don't fight it
	if (!userId.value) return true

	// the cached user can predate the profile being filled, so confirm with the API once
	if (!pendingCheck) {
		pendingCheck = getProfile({'userId:eq': userId.value})
			.then((rows: any) => {
				// the request 401'd and the session was dropped: not our redirect to make
				if (!userId.value || !token.value) return true
				const profile = Array.isArray(rows) ? rows[0] : rows
				if (!isProfileFilled(profile)) return false
				user.value = {...(user.value || {}), profile}
				complete.value = true
				return true
			})
			// never trap the user behind a failed request
			.catch(() => true)
			.finally(() => {
				pendingCheck = null
			})
	}

	return pendingCheck
}
